// The notification webhooks' view model (SCR-233, API-235, REQ-088). Pure and DOM-free apart from
// the reads and writes at the end, which take the client (tests/unit/notifications/model.test.ts);
// rendering lives in the .tsx files beside this one; where the screen sits is each shell's (IA-2).
//
// Secrets: the signing secret comes back only from create and rotate. It lives in the one-time
// dialog's state (`SecretReveal`) and nowhere else — never in the list, the row, the form or any
// storage — and closing the dialog drops it. The URL is a credential too (a Slack URL is): the API
// returns only `urlHost`, and the edit form starts with an empty URL that, left empty, is not sent.
// Webhook payloads and delivery rows carry no money, so this module has no money policy to apply.
import type { ApiClient } from "../../api/client";
import type { Surface } from "../../api/org-context";
import type { components } from "../../api/schema";
import type { FieldErrors } from "../../primitives/forms";
import type { ChipTone } from "../../primitives/page";
import type { ScopeMe } from "../../scope/scope";
import { asApiError, isNotAvailable, utcMoment } from "../quotas/model";

/** What the webhook rules read of the principal: its role. */
type Me = Pick<ScopeMe, "role">;

type Schemas = components["schemas"];
export type NotificationWebhook = Schemas["NotificationWebhook"];
export type NotificationWebhookWithSecret = Schemas["NotificationWebhookWithSecret"];
export type CreateNotificationWebhookRequest = Schemas["CreateNotificationWebhookRequest"];
export type PatchNotificationWebhookRequest = Schemas["PatchNotificationWebhookRequest"];
export type WebhookFormat = Schemas["WebhookFormat"];
export type WebhookStatus = Schemas["WebhookStatus"];
export type WebhookEventType = Schemas["WebhookEventType"];
export type WebhookDelivery = Schemas["WebhookDelivery"];
export type WebhookDeliveryList = Schemas["WebhookDeliveryList"];
export type WebhookTestResult = Schemas["WebhookTestResult"];
export type WebhookOwner = Schemas["QuotaOwner"];

export { asApiError, isNotAvailable, utcMoment };

// ---------------------------------------------------------------------------------------------
// Labels

export const WEBHOOK_LIMIT = 10;
export const FORMATS: readonly WebhookFormat[] = ["generic", "slack"];
export const EVENT_TYPES: readonly WebhookEventType[] = ["quota.threshold_crossed", "quota.exhausted", "quota.grant_created"];

const FORMAT_LABEL: Readonly<Record<WebhookFormat, string>> = { generic: "Generic JSON", slack: "Slack" };
const EVENT_LABEL: Readonly<Record<WebhookEventType, string>> = {
  "quota.threshold_crossed": "Warning crossed",
  "quota.exhausted": "Limit reached",
  "quota.grant_created": "Grant added",
};
const STATUS: Readonly<Record<WebhookStatus, { label: string; tone: ChipTone }>> = {
  active: { label: "Active", tone: "green" },
  disabled: { label: "Disabled", tone: "grey" },
  failing: { label: "Failing", tone: "red" },
};
const DELIVERY_STATUS: Readonly<Record<Schemas["WebhookDeliveryStatus"], { label: string; tone: ChipTone }>> = {
  pending: { label: "Retrying", tone: "amber" },
  delivered: { label: "Delivered", tone: "green" },
  abandoned: { label: "Gave up", tone: "red" },
};

export const formatLabel = (format: WebhookFormat): string => FORMAT_LABEL[format];
export const eventLabel = (event: WebhookEventType): string => EVENT_LABEL[event];

const ERROR_CLASS_LABEL: Readonly<Record<string, string>> = {
  timeout: "Timed out",
  connect: "Could not connect",
  address_refused: "Address refused (private or metadata)",
  status_4xx: "Refused by the receiver (4xx)",
  status_5xx: "Receiver error (5xx)",
  tls: "TLS failure",
};

/** A content-free failure class in words; an unknown class as the API sent it. */
export function errorClassLabel(errorClass: string | null): string {
  if (errorClass === null) return "—";
  return ERROR_CLASS_LABEL[errorClass] ?? errorClass;
}

function when(iso: string | null): string {
  return iso === null ? "—" : utcMoment(iso);
}

// ---------------------------------------------------------------------------------------------
// Who may change what: an org webhook is the org admin's or a super-admin's; a platform-owned one
// the super-admin's alone; a platform principal changes none (API-235).

export function canWriteWebhooks(me: Me): boolean {
  return me.role !== "platform";
}

export function canWriteWebhook(me: Me, webhook: Pick<NotificationWebhook, "owner">): boolean {
  if (webhook.owner === "platform") return me.role === "super-admin";
  return canWriteWebhooks(me);
}

/** Owner is a staff-only control, and only a super-admin may pick `platform`. */
export function ownerChoices(surface: Surface, me: Me): readonly WebhookOwner[] {
  return surface === "staff" && me.role === "super-admin" ? ["org", "platform"] : ["org"];
}

// ---------------------------------------------------------------------------------------------
// The list

export interface WebhookRow {
  id: string;
  name: string;
  host: string;
  format: string;
  events: string;
  statusLabel: string;
  statusTone: ChipTone;
  failing: boolean;
  lastSuccess: string;
  lastFailure: string;
  consecutiveFailures: number;
  ownerLabel: string;
  writable: boolean;
  webhook: NotificationWebhook;
}

export function webhookRow(webhook: NotificationWebhook, me: Me): WebhookRow {
  return {
    id: webhook.id,
    name: webhook.name,
    host: webhook.urlHost,
    format: formatLabel(webhook.format),
    events: EVENT_TYPES.filter((event) => webhook.eventTypes.includes(event)).map(eventLabel).join(", "),
    statusLabel: STATUS[webhook.status].label,
    statusTone: STATUS[webhook.status].tone,
    failing: webhook.status === "failing",
    lastSuccess: when(webhook.lastSuccessAt),
    lastFailure: when(webhook.lastFailureAt),
    consecutiveFailures: webhook.consecutiveFailures,
    ownerLabel: webhook.owner === "platform" ? "AllCode" : "Organization",
    writable: canWriteWebhook(me, webhook),
    webhook,
  };
}

/** Failing first (they need a look), then by name. */
export function webhookRows(webhooks: readonly NotificationWebhook[], me: Me): WebhookRow[] {
  return webhooks.map((webhook) => webhookRow(webhook, me)).sort((a, b) => Number(b.failing) - Number(a.failing) || a.name.localeCompare(b.name));
}

export function atLimit(webhooks: readonly NotificationWebhook[]): boolean {
  return webhooks.length >= WEBHOOK_LIMIT;
}

export const LIMIT_MESSAGE = `This organization already has ${WEBHOOK_LIMIT} webhooks, the most it may have. Delete one to add another.`;

// ---------------------------------------------------------------------------------------------
// The form (create and edit), strings as typed, validated against the spec's bounds

export const LIMITS = { name: 100, url: 2000 } as const;

export interface WebhookFormValues {
  name: string;
  /** Empty on an edit means "keep the stored URL". */
  url: string;
  format: WebhookFormat;
  eventTypes: WebhookEventType[];
  owner: WebhookOwner;
}

export type WebhookFormMode = { kind: "create" } | { kind: "edit"; webhook: NotificationWebhook };

export function blankWebhookForm(): WebhookFormValues {
  return { name: "", url: "", format: "generic", eventTypes: [...EVENT_TYPES], owner: "org" };
}

export function formFromWebhook(webhook: NotificationWebhook): WebhookFormValues {
  return { name: webhook.name, url: "", format: webhook.format, eventTypes: [...webhook.eventTypes], owner: webhook.owner };
}

export function toggleEvent(values: WebhookFormValues, event: WebhookEventType, on: boolean): WebhookFormValues {
  const set = new Set(values.eventTypes);
  if (on) set.add(event);
  else set.delete(event);
  return { ...values, eventTypes: EVENT_TYPES.filter((type) => set.has(type)) };
}

/** The client's check of an https URL; the server also refuses private addresses (422). */
export function urlProblem(input: string): string | null {
  const text = input.trim();
  if (text.length > LIMITS.url) return `At most ${LIMITS.url} characters.`;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return "A full URL, starting with https://.";
  }
  if (url.protocol !== "https:") return "Only https:// URLs are accepted.";
  if (url.username !== "" || url.password !== "") return "No user name or password in the URL.";
  return null;
}

export function validateWebhookForm(values: WebhookFormValues, mode: WebhookFormMode, owners: readonly WebhookOwner[]): FieldErrors {
  const errors: FieldErrors = {};
  const name = [...values.name.trim()].length;
  if (name === 0) errors["name"] = "Required.";
  else if (name > LIMITS.name) errors["name"] = `At most ${LIMITS.name} characters.`;
  const keepUrl = mode.kind === "edit" && values.url.trim() === "";
  if (!keepUrl) {
    const problem = values.url.trim() === "" ? "Required." : urlProblem(values.url);
    if (problem) errors["url"] = problem;
  }
  if (values.eventTypes.length === 0) errors["eventTypes"] = "Pick at least one event.";
  if (mode.kind === "create" && !owners.includes(values.owner)) errors["owner"] = "Only a super-admin sets an AllCode webhook.";
  return errors;
}

/** The body of `createNotificationWebhook`; call only on a valid form. Owner is sent only when it is not the default. */
export function toCreateRequest(values: WebhookFormValues): CreateNotificationWebhookRequest {
  const request: CreateNotificationWebhookRequest = { name: values.name.trim(), url: values.url.trim(), format: values.format, eventTypes: [...values.eventTypes] };
  if (values.owner === "platform") request.owner = "platform";
  return request;
}

/** Only what changed; a URL only when one was typed. */
export function toPatchRequest(values: WebhookFormValues, webhook: NotificationWebhook): PatchNotificationWebhookRequest {
  const patch: PatchNotificationWebhookRequest = {};
  const name = values.name.trim();
  if (name !== webhook.name) patch.name = name;
  const url = values.url.trim();
  if (url !== "") patch.url = url;
  if (values.format !== webhook.format) patch.format = values.format;
  const events = [...values.eventTypes].sort();
  if (events.join(",") !== [...webhook.eventTypes].sort().join(",")) patch.eventTypes = values.eventTypes;
  return patch;
}

export function patchChanges(patch: PatchNotificationWebhookRequest): boolean {
  return Object.keys(patch).length > 0;
}

/** Enable a disabled or failing webhook, disable an active one. */
export function statusToggle(webhook: Pick<NotificationWebhook, "status">): { status: "active" | "disabled"; label: string } {
  return webhook.status === "active" ? { status: "disabled", label: "Disable" } : { status: "active", label: webhook.status === "failing" ? "Re-enable" : "Enable" };
}

// ---------------------------------------------------------------------------------------------
// The one-time secret

export interface SecretReveal {
  webhookName: string;
  secret: string;
  copied: boolean;
}

/** Takes the secret out of a create or rotate answer; the webhook kept for the list has none. */
export function revealSecret(answer: NotificationWebhookWithSecret): { webhook: NotificationWebhook; reveal: SecretReveal } {
  const { secret, ...webhook } = answer;
  return { webhook, reveal: { webhookName: answer.name, secret, copied: false } };
}

export function markCopied(reveal: SecretReveal): SecretReveal {
  return { ...reveal, copied: true };
}

// ---------------------------------------------------------------------------------------------
// The test send

export interface TestOutcome {
  delivered: boolean;
  tone: ChipTone;
  headline: string;
  detail: string;
}

export function testOutcome(result: WebhookTestResult): TestOutcome {
  const status = result.statusCode === null ? "no response" : `HTTP ${result.statusCode}`;
  const at = utcMoment(result.attemptedAt);
  if (result.delivered) return { delivered: true, tone: "green", headline: "Delivered", detail: `The receiver answered ${status} at ${at}.` };
  const why = result.errorClass === null ? status : `${errorClassLabel(result.errorClass)}, ${status}`;
  return { delivered: false, tone: "red", headline: "Not delivered", detail: `${why} at ${at}.` };
}

// ---------------------------------------------------------------------------------------------
// Deliveries

export const DELIVERIES_PAGE = 100;

export interface DeliveryRow {
  id: string;
  event: string;
  eventId: number;
  statusLabel: string;
  statusTone: ChipTone;
  attempts: number;
  lastStatus: string;
  errorClass: string;
  created: string;
  delivered: string;
  nextAttempt: string;
}

export function deliveryRow(delivery: WebhookDelivery): DeliveryRow {
  return {
    id: delivery.id,
    event: eventLabel(delivery.eventType),
    eventId: delivery.eventId,
    statusLabel: DELIVERY_STATUS[delivery.status].label,
    statusTone: DELIVERY_STATUS[delivery.status].tone,
    attempts: delivery.attempts,
    lastStatus: delivery.lastStatusCode === null ? "—" : String(delivery.lastStatusCode),
    errorClass: errorClassLabel(delivery.lastErrorClass),
    created: when(delivery.createdAt),
    delivered: when(delivery.deliveredAt),
    nextAttempt: delivery.status === "pending" ? when(delivery.nextAttemptAt) : "—",
  };
}

export function deliveriesQuery(cursor: string | null): { limit: number; cursor?: string } {
  return cursor ? { limit: DELIVERIES_PAGE, cursor } : { limit: DELIVERIES_PAGE };
}

// ---------------------------------------------------------------------------------------------
// Errors

export const NOT_AVAILABLE = "Notification webhooks are not available yet on this Nexus API. They appear here once the server is updated.";

export function isWebhookLimit(error: unknown): boolean {
  return asApiError(error).code === "webhook_limit";
}

/** A sentence for a refused write; `invalid_webhook_url` shows beside the URL field instead. */
export function webhookWriteFailure(error: unknown): string {
  const api = asApiError(error);
  switch (api.code) {
    case "webhook_limit":
      return LIMIT_MESSAGE;
    case "invalid_webhook_url":
      return "This URL is refused: it must be https, carry no user name or password, and point at a public address.";
    case "forbidden":
      return "Your role cannot change this webhook.";
    default:
      return isNotAvailable(api) ? NOT_AVAILABLE : api.message;
  }
}

/** The URL field's message for an `invalid_webhook_url` answer, else null. */
export function urlFieldFailure(error: unknown): string | null {
  return asApiError(error).code === "invalid_webhook_url" ? webhookWriteFailure(error) : null;
}

// ---------------------------------------------------------------------------------------------
// Reads and writes (API-235)

export async function loadWebhooks(client: ApiClient, orgId: string): Promise<NotificationWebhook[]> {
  return (await client.get("/v1/orgs/{orgId}/notification-webhooks", { path: { orgId } })).items;
}

export function createWebhook(client: ApiClient, orgId: string, request: CreateNotificationWebhookRequest): Promise<NotificationWebhookWithSecret> {
  return client.post("/v1/orgs/{orgId}/notification-webhooks", { path: { orgId }, body: request });
}

function webhookPath(webhook: Pick<NotificationWebhook, "orgId" | "id">) {
  return { orgId: webhook.orgId, webhookId: webhook.id };
}

export function patchWebhook(client: ApiClient, webhook: NotificationWebhook, patch: PatchNotificationWebhookRequest): Promise<NotificationWebhook> {
  return client.patch("/v1/orgs/{orgId}/notification-webhooks/{webhookId}", { path: webhookPath(webhook), body: patch });
}

export async function deleteWebhook(client: ApiClient, webhook: NotificationWebhook): Promise<void> {
  await client.delete("/v1/orgs/{orgId}/notification-webhooks/{webhookId}", { path: webhookPath(webhook) });
}

export function rotateSecret(client: ApiClient, webhook: NotificationWebhook): Promise<NotificationWebhookWithSecret> {
  return client.post("/v1/orgs/{orgId}/notification-webhooks/{webhookId}/rotate-secret", { path: webhookPath(webhook) });
}

export function testWebhook(client: ApiClient, webhook: NotificationWebhook): Promise<WebhookTestResult> {
  return client.post("/v1/orgs/{orgId}/notification-webhooks/{webhookId}/test", { path: webhookPath(webhook) });
}

export function loadDeliveries(client: ApiClient, webhook: NotificationWebhook, cursor: string | null): Promise<WebhookDeliveryList> {
  return client.get("/v1/orgs/{orgId}/notification-webhooks/{webhookId}/deliveries", { path: webhookPath(webhook), query: deliveriesQuery(cursor) });
}
